import { forwardRef, HTMLAttributes, useCallback, useMemo, useRef, useState } from 'react'
import { Transition, TransitionStatus } from 'react-transition-group'
import { useClickAway, useIsomorphicLayoutEffect, useKey } from 'react-use'
import gsap from 'gsap'
import Lenis from 'lenis'
import mergeRefs from 'merge-refs'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Form from '@/components/base/Form'
import LegalsMenu from '@/components/base/LegalsMenu'
import SocialMenu from '@/components/base/SocialMenu'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'
import { stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'

/**
 * Newsletter
 */
export type NewsletterProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    state: TransitionStatus
    onClose: () => void
  }>

const Newsletter = forwardRef<HTMLDivElement, NewsletterProps>(
  ({ state, className, onClose, ...props }, ref) => {
    const { strings } = useStore()
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)

    useKey('Backspace', () => onClose?.())

    return (
      <div
        ref={mergeRefs(ref, wrapper)}
        className={cn(
          'relative flex h-full w-full shrink-0 grow overflow-auto px-margin pointer:scrollbar-hide',
          'transition-transform duration-500 ease-in-out-cubic will-change-transform',
          'translate-x-full',
          {
            'absolute left-0 top-0 translate-x-full': state === 'exiting',
            'translate-x-0': state === 'entering' || state === 'entered'
          },
          className
        )}
        {...props}
      >
        <div ref={content} className="my-auto grow p-margin md:p-0">
          <div className="-mt-[var(--header-height)] grid gap-y-spacing-xl md:mt-0">
            <div className="relative md:absolute md:left-margin md:top-[3.65rem]">
              <button className="-m-[1rem] inline-flex p-[1rem]" onClick={onClose}>
                <span className="sr-only">Back</span>
                <SVGIcon symbol="arrow-left" className="h-[.8rem] w-[4.1rem] fill-white-light" />
              </button>
            </div>

            <div className="grid gap-y-spacing-md">
              <div className="grid gap-y-spacing-sm">
                <Typography component="h4" variant="h4" className="" onClick={onClose}>
                  {strings?.newsletter.title}
                </Typography>
                <Typography variant="regular" paragraph className="">
                  {strings?.newsletter.description}
                </Typography>
              </div>

              <hr className="opacity-30" />

              <div>
                <Form
                  form_id={strings?.newsletter.form_id}
                  fields={strings?.newsletter.fields}
                  submit={strings?.newsletter.submit}
                  className="[&_.status-message]:absolute [&_.status-message]:left-0 [&_.status-message]:top-full [&_.status-message]:mt-spacing-xs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

/**
 * Navigation
 */
export type NavigationProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    state: TransitionStatus
    menuState: TransitionStatus
    onNewsletterClick?: () => void
  }>

const Navigation = forwardRef<HTMLDivElement, NavigationProps>(
  ({ state, menuState, className, onNewsletterClick, ...props }, ref) => {
    const { asPath } = useRouter()
    const { strings, menu } = useStore()
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)

    const mainMenu1 = useMemo(() => menu?.main_menu_01.menu || [], [menu])
    const mainMenu2 = useMemo(() => menu?.main_menu_02.menu || [], [menu])
    const mainMenu3 = useMemo(() => menu?.main_menu_03.menu || [], [menu])

    const isActive = useCallback((url: string) => stripSlash(url) === stripSlash(asPath), [asPath])

    useIsomorphicLayoutEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        wrapper: wrapper.current,
        content: content.current
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      return () => {
        gsap.ticker.remove(tick)
        lenis.destroy()
      }
    }, [])

    return (
      <div
        ref={mergeRefs(ref, wrapper)}
        className={cn(
          'relative flex h-full w-full shrink-0 grow overflow-auto px-margin pointer:scrollbar-hide',
          'transition-transform duration-500 ease-in-out-cubic will-change-transform',
          'translate-x-0',
          {
            'absolute left-0 top-0 -translate-x-full': state === 'exiting',
            'translate-x-0': state === 'entering',
            '-translate-x-full': state === 'exited'
          },
          className
        )}
        {...props}
      >
        <div
          ref={content}
          className="my-auto grid grow gap-y-spacing-md p-margin md:gap-y-spacing-lg md:p-0"
          // className="grid grow gap-y-spacing-2xl px-margin py-spacing-lg md:gap-y-spacing-2xl md:px-0"
        >
          <nav>
            <ul>
              <li
                className={cn('flex', 'transition-transform duration-500 ease-out-cubic', {
                  'translate-x-[2rem]': menuState === 'exiting' || menuState === 'exited',
                  'translate-x-0': menuState === 'entering' || menuState === 'entered'
                })}
                style={{
                  transitionDelay: `${300}ms`
                }}
              >
                <Link
                  href="/"
                  scroll={false}
                  className={cn(
                    '-my-2 inline-flex overflow-hidden py-2',
                    'transition-opacity duration-300 ease-out-cubic',
                    'hover:opacity-50',
                    {
                      'pointer-events-none opacity-20': isActive('/')
                    }
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex',
                      'transition-transform duration-500 ease-out-cubic',
                      {
                        'translate-y-[150%] md:translate-y-[130%]':
                          menuState === 'exiting' || menuState === 'exited',
                        'translate-y-0': menuState === 'entering' || menuState === 'entered'
                      }
                    )}
                    style={{ transitionDelay: `${300}ms` }}
                  >
                    <Typography variant="h4">{strings?.home}</Typography>
                  </span>
                </Link>
              </li>
            </ul>
          </nav>

          <nav>
            <ul className="grid">
              <li
                className={cn(
                  'flex overflow-hidden py-2',
                  'transition-transform duration-500 ease-out-cubic',
                  {
                    'translate-x-[2rem]': menuState === 'exiting' || menuState === 'exited',
                    'translate-x-0': menuState === 'entering' || menuState === 'entered'
                  }
                )}
                style={{
                  transitionDelay: `${300}ms`
                }}
              >
                <span
                  className={cn('inline-flex', 'transition-transform duration-500 ease-out-cubic', {
                    'translate-y-[150%] md:translate-y-[130%]':
                      menuState === 'exiting' || menuState === 'exited',
                    'translate-y-0': menuState === 'entering' || menuState === 'entered'
                  })}
                  style={{ transitionDelay: `${300}ms` }}
                >
                  <Typography variant="regular" className="text-text-secondary-dark">
                    {strings?.services}
                  </Typography>
                </span>
              </li>
              {mainMenu1.map(({ title, url }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn(
                    'relative flex py-spacing-xs',
                    'transition-transform duration-500 ease-out-cubic',
                    {
                      'translate-x-[2rem]': menuState === 'exiting' || menuState === 'exited',
                      'translate-x-0': menuState === 'entering' || menuState === 'entered'
                    }
                  )}
                  style={{
                    transitionDelay: `${300 + 50 * index}ms`
                  }}
                >
                  <Link
                    href={url}
                    scroll={false}
                    className={cn(
                      '-my-2 inline-flex overflow-hidden py-2',
                      'transition-all duration-300 ease-out-cubic',
                      'hover:text-text-accent-dark',
                      {
                        'pointer-events-none opacity-20': isActive(url)
                      }
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex',
                        'transition-transform duration-500 ease-out-cubic',
                        {
                          'translate-y-[150%] md:translate-y-[130%]':
                            menuState === 'exiting' || menuState === 'exited',
                          'translate-y-0': menuState === 'entering' || menuState === 'entered'
                        }
                      )}
                      style={{ transitionDelay: `${300 + 50 * index}ms` }}
                    >
                      <Typography variant="h4">{title}</Typography>
                    </span>
                  </Link>
                  <hr
                    className={cn(
                      'absolute bottom-0 left-0 w-full origin-left opacity-30',
                      'transition-transform duration-500 ease-out-cubic',
                      {
                        'scale-x-0': menuState === 'exiting' || menuState === 'exited',
                        'scale-x-100': menuState === 'entering' || menuState === 'entered'
                      }
                    )}
                    style={{ transitionDelay: `${300 + 100 * index}ms` }}
                  />
                </li>
              ))}
            </ul>
          </nav>

          <nav>
            <ul className="grid gap-y-spacing-xs">
              {mainMenu2.map(({ title, url }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn('flex', 'transition-transform duration-500 ease-out-cubic', {
                    'translate-x-[2rem]': menuState === 'exiting' || menuState === 'exited',
                    'translate-x-0': menuState === 'entering' || menuState === 'entered'
                  })}
                  style={{
                    transitionDelay: `${300 + 50 * (mainMenu1.length + index)}ms`
                  }}
                >
                  <Link
                    href={url}
                    scroll={false}
                    className={cn(
                      '-my-2 inline-flex overflow-hidden py-2',
                      'transition-all duration-300 ease-out-cubic',
                      'hover:text-text-accent-dark',
                      {
                        'pointer-events-none opacity-20': isActive(url)
                      }
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex',
                        'transition-transform duration-500 ease-out-cubic',
                        {
                          'translate-y-[150%] md:translate-y-[130%]':
                            menuState === 'exiting' || menuState === 'exited',
                          'translate-x-0': menuState === 'entering' || menuState === 'entered'
                        }
                      )}
                      style={{
                        transitionDelay: `${300 + 50 * (mainMenu1.length + index)}ms`
                      }}
                    >
                      <Typography variant="h4">{title}</Typography>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav>
            <ul className="grid gap-y-spacing-xs">
              {mainMenu3.map(({ title, url }, index) => (
                <li
                  key={`menu-item-${index}`}
                  className={cn('flex', 'transition-transform duration-500 ease-out-cubic', {
                    'translate-x-[2rem]': menuState === 'exiting' || menuState === 'exited',
                    'translate-x-0': menuState === 'entering' || menuState === 'entered'
                  })}
                  style={{
                    transitionDelay: `${300 + 50 * (mainMenu1.length + mainMenu2.length + index)}ms`
                  }}
                >
                  <Link
                    href={url}
                    scroll={false}
                    className={cn(
                      'group -my-2 inline-flex overflow-hidden py-2',
                      'transition-all duration-300 ease-out-cubic',
                      'text-text-secondary-dark',
                      'hover:text-text-accent-dark',
                      {
                        'pointer-events-none opacity-40': isActive(url)
                      }
                    )}
                    onClick={(event) => {
                      if (url === '#newsletter') {
                        event.preventDefault()
                        onNewsletterClick?.()
                      }
                    }}
                  >
                    <span
                      className={cn(
                        'inline-flex',
                        'transition-transform duration-500 ease-out-cubic',
                        {
                          'translate-y-[150%] md:translate-y-[130%]':
                            menuState === 'exiting' || menuState === 'exited',
                          'translate-y-0': menuState === 'entering' || menuState === 'entered'
                        }
                      )}
                      style={{
                        transitionDelay: `${300 + 50 * (mainMenu1.length + mainMenu2.length + index)}ms`
                      }}
                    >
                      <Typography variant="h6" className="">
                        {title}
                      </Typography>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <LegalsMenu
            className={cn(
              '[&_.label]:transition-transform [&_.label]:duration-500 [&_.label]:ease-out-cubic',
              {
                '[&_.label]:translate-x-[2rem] [&_.label]:translate-y-[180%]':
                  menuState === 'exiting' || menuState === 'exited',
                '[&_.label]:translate-x-0 [&_.label]:translate-y-0':
                  menuState === 'entering' || menuState === 'entered'
              }
            )}
            revealDelay={mainMenu1.length + mainMenu2.length + mainMenu3.length}
          />

          <SocialMenu
            className={cn(
              '[&_.icon]:transition-transform [&_.icon]:duration-500 [&_.icon]:ease-out-cubic',
              {
                '[&_.icon]:translate-x-[2rem] [&_.icon]:translate-y-[150%]':
                  menuState === 'exiting' || menuState === 'exited',
                '[&_.icon]:translate-x-0 [&_.icon]:translate-y-0':
                  menuState === 'entering' || menuState === 'entered'
              }
            )}
            revealDelay={mainMenu1.length + mainMenu2.length + mainMenu3.length}
          />
        </div>
      </div>
    )
  }
)

/**
 * Menu
 */
export type MenuProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    state: TransitionStatus
    onClose: () => void
  }>

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, className, onClose, ...props }, ref) => {
    const [newsletterOpen, setNewsletterOpen] = useState(false)
    const panel = useRef<HTMLDivElement>(null!)
    const navigation = useRef<HTMLDivElement>(null!)
    const newsletter = useRef<HTMLDivElement>(null!)

    useClickAway(panel, () => onClose?.())

    useKey('Escape', () => onClose?.())

    return (
      <div
        ref={ref}
        className={cn(
          'static',
          'before:fixed before:left-0 before:top-0 before:z-menu before:h-full before:w-full before:bg-black-dark/0',
          className
        )}
        {...props}
      >
        <div
          ref={panel}
          className={cn(
            'fixed bottom-0 right-0 top-[var(--header-height)] z-menu bg-black-dark md:top-0',
            'flex w-full overflow-hidden md:w-auto md:max-w-[calc(46rem_+_var(--margin)_*_2)]',
            'transition-transform duration-500 ease-in-out-cubic will-change-transform',
            {
              'translate-x-full duration-0': state === 'entering',
              'translate-x-0': state === 'entered',
              'translate-x-full': state === 'exiting'
            }
          )}
        >
          <Transition nodeRef={navigation} in={!newsletterOpen} timeout={500} unmountOnExit>
            {(swapState) => (
              <Navigation
                ref={navigation}
                state={swapState}
                menuState={state}
                onNewsletterClick={() => setNewsletterOpen(true)}
              />
            )}
          </Transition>

          <Transition nodeRef={newsletter} in={newsletterOpen} timeout={500} unmountOnExit>
            {(state) => (
              <Newsletter ref={newsletter} state={state} onClose={() => setNewsletterOpen(false)} />
            )}
          </Transition>
        </div>
      </div>
    )
  }
)

export default Menu
